import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Dialog, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useTranslation } from "react-i18next";
import "./AfspraakModal.css";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 30-minute slots from 09:00 up to and including 17:00
const TIME_SLOTS = [];
for (let hour = 9; hour <= 17; hour++) {
  TIME_SLOTS.push(`${String(hour).padStart(2, "0")}:00`);
  if (hour < 17) {
    TIME_SLOTS.push(`${String(hour).padStart(2, "0")}:30`);
  }
}

const MEETING_TYPES = [
  { key: "video", Icon: VideocamOutlinedIcon },
  { key: "phone", Icon: PhoneOutlinedIcon },
];

const LANGUAGES = [
  { key: "nl", label: "Nederlands" },
  { key: "en", label: "English" },
  { key: "fa", label: "فارسی (Persian)" },
  { key: "es", label: "Español" },
  { key: "fr", label: "Français" },
  { key: "tr", label: "Türkçe" },
];

function formatDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function todayString() {
  return formatDate(new Date());
}

function isWeekend(dateString) {
  const weekday = new Date(`${dateString}T00:00:00`).getDay();
  return weekday === 0 || weekday === 6;
}

function slotsForDate(dateString) {
  if (dateString !== todayString()) return TIME_SLOTS;
  const now = new Date();
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  return TIME_SLOTS.filter((slot) => {
    const [hour, minute] = slot.split(":").map(Number);
    return hour * 60 + minute > minutesNow;
  });
}

// Earliest pickable date: today, or tomorrow once today's last slot has passed
function minSelectableDate() {
  if (slotsForDate(todayString()).length > 0) return todayString();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatDate(tomorrow);
}

const AfspraakModal = ({ visible, setVisible }) => {
  const { requestId } = useParams();
  const { t, i18n } = useTranslation();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [language, setLanguage] = useState("");
  const [message, setMessage] = useState("");
  const [shareConsent, setShareConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (visible) {
      setDate("");
      setTime("");
      setSelectedType(null);
      setLanguage(
        LANGUAGES.some((lang) => lang.key === i18n.language)
          ? i18n.language
          : ""
      );
      setMessage(t("afspraakModal.berichtPlaceholder"));
      setShareConsent(false);
      setSubmitting(false);
      setSubmitted(false);
      setSubmitError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dateError = date && isWeekend(date);
  const availableSlots = slotsForDate(date);

  const handleSubmit = async () => {
    if (dateError) return;

    setSubmitting(true);
    setSubmitError(false);
    try {
      const response = await fetch(
        `${REACT_APP_BACKEND_URL}/run/book_meeting_welocate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            request_id: requestId,
            datetime: `${date}T${time}`,
            meetingType: selectedType,
            language: language,
            message: message,
            shareConsent: shareConsent,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to send appointment request");
      }
      setSubmitted(true);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={() => setVisible(false)}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          width: "560px",
          maxWidth: "94vw",
        },
      }}
    >
      <div className="afspraak-modal">
        <div className="afspraak-modal-header">
          <h2>{t("afspraakButton")}</h2>
          <IconButton
            aria-label={t("sluiten")}
            onClick={() => setVisible(false)}
            sx={{ backgroundColor: "#F3F4F6", color: "#454545" }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {submitted ? (
          <div className="afspraak-success">
            <CheckCircleIcon sx={{ color: "#27CA40", fontSize: "48px" }} />
            <h4>{t("afspraakModal.successTitel")}</h4>
            <span>{t("afspraakModal.successText")}</span>
            <button
              type="button"
              className="afspraak-submit-btn"
              onClick={() => setVisible(false)}
            >
              {t("sluiten")}
            </button>
          </div>
        ) : (
          <React.Fragment>
            <div className="afspraak-modal-section">
              <h4>{t("afspraakModal.wanneerVraag")}</h4>
              <div className="afspraak-datetime">
                <label className="afspraak-datetime-field">
                  <span>{t("afspraakModal.datumLabel")}</span>
                  <input
                    type="date"
                    className={dateError ? "invalid" : ""}
                    value={date}
                    min={minSelectableDate()}
                    onChange={(e) => {
                      setDate(e.target.value);
                      if (time && !slotsForDate(e.target.value).includes(time)) {
                        setTime("");
                      }
                    }}
                  />
                </label>
                <label className="afspraak-datetime-field">
                  <span>{t("afspraakModal.tijdLabel")}</span>
                  <select
                    className="afspraak-select"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  >
                    <option value="" disabled>
                      {t("afspraakModal.tijdPlaceholder")}
                    </option>
                    {availableSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {dateError ? (
                <span className="afspraak-error">
                  {t("afspraakModal.werkdagError")}
                </span>
              ) : (
                <span className="afspraak-hint">
                  {t("afspraakModal.beschikbaarheidHint")}
                </span>
              )}
            </div>

            <div className="afspraak-modal-section">
              <h4>{t("afspraakModal.typeVraag")}</h4>
              <div className="afspraak-types">
                {MEETING_TYPES.map(({ key, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    className={`afspraak-type ${
                      selectedType === key ? "selected" : ""
                    }`}
                    onClick={() => setSelectedType(key)}
                  >
                    <Icon sx={{ fontSize: "22px" }} />
                    {t(`afspraakModal.type.${key}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="afspraak-modal-section">
              <h4>{t("afspraakModal.taalVraag")}</h4>
              <select
                className="afspraak-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="" disabled>
                  {t("afspraakModal.taalPlaceholder")}
                </option>
                {LANGUAGES.map((lang) => (
                  <option key={lang.key} value={lang.key}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="afspraak-modal-section">
              <h4>{t("afspraakModal.berichtLabel")}</h4>
              <textarea
                className="afspraak-message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <label className="afspraak-consent">
              <input
                type="checkbox"
                checked={shareConsent}
                onChange={(e) => setShareConsent(e.target.checked)}
              />
              <span>{t("afspraakModal.consentText")}</span>
            </label>

            {submitError && (
              <span className="afspraak-error">
                {t("afspraakModal.errorText")}
              </span>
            )}

            <button
              type="button"
              className="afspraak-submit-btn"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !date ||
                dateError ||
                !time ||
                !language ||
                !selectedType ||
                !shareConsent
              }
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                <CalendarMonthIcon sx={{ fontSize: "20px" }} />
              )}
              {t("afspraakModal.verstuur")}
            </button>
          </React.Fragment>
        )}
      </div>
    </Dialog>
  );
};

export default AfspraakModal;
